import { Assignment, Business, Folder, Home, Layers, TrendingUp } from "@mui/icons-material";
import { Breadcrumbs, Chip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { getProcedureRole } from "../../api/procedure/getProcedureRole";
import { getStage, getStageRole } from "../../api/stage";
import { useTranslationHook } from "../../hooks";
import { ProcedureRole } from "../../types/model/procedure";
import { Stage, StageRole } from "../../types/model/stage";
import { StyledBreadcrumContainer } from "./StyledBreadcrumContainer";

export const Breadcrumb = () => {
  const {
    accountId,
    projectId,
    activityId,
    procedureId,
    stageId,
  } = useParams<{
    accountId?: string;
    projectId?: string;
    activityId?: string;
    procedureId?: string;
    stageId?: string;
  }>();

  const { t } = useTranslationHook();
  const navigate = useNavigate();

  const [stageRole, setStageRole] = useState<StageRole | undefined>(undefined);
  const [procedureRole, setProcedureRole] = useState<ProcedureRole | undefined>(undefined);
  const [roleLoading, setRoleLoading] = useState(false);

  const [stage, setStage] = useState<Stage | undefined>(undefined);
  const [stageLoading, setStageLoading] = useState(false);

  const isTender = stageRole?.isTender || procedureRole?.isTender;

  const navigateTo = (type: string) => {
    if (type === "home")
      navigate("/home");

    if (type === "account" && accountId)
      navigate(`/accounts/${accountId}`);

    if (type === "project" && accountId && projectId)
      navigate(`/accounts/${accountId}/projects/${projectId}`);

    if (type === "activity" && accountId && projectId && activityId)
      navigate(`/accounts/${accountId}/projects/${projectId}/activities/${activityId}`);

    if (type === "procedure" && accountId && projectId && activityId && procedureId)
      navigate(`/accounts/${accountId}/projects/${projectId}/activities/${activityId}/procedures/${procedureId}`);
  };

  const renderItem = (key: string, label: string, type: string, icon: React.ReactElement, isLink: boolean) => {
    return (
      <Chip
        key={key}
        label={label}
        icon={React.cloneElement(icon, { sx: { fontSize: "1.1rem" } })}
        variant={isLink ? "outlined" : "filled"}
        color="primary"
        onClick={isLink ? () => navigateTo(type) : undefined}
        size="medium"
        sx={{
          fontWeight: 600,
          px: 1,
        }}
      />
    );
  };

  const buildBreadcrumb = () => {
    if (roleLoading || stageLoading) {
      return <Typography sx={{ color: "text.primary" }}>...</Typography>;
    }

    const items: React.ReactElement[] = [];

    const hasAnyId = !!(accountId || projectId || activityId || procedureId || stageId);

    items.push(
      renderItem("home", t("generic.home"), "home", <Home />, hasAnyId)
    );

    if (accountId && !isTender) {
      items.push(
        renderItem(
          "account",
          t("generic.account"),
          "account",
          <Business />,
          !!(stageId || procedureId || activityId || projectId)
        )
      );
    }

    if (projectId && !isTender) {
      items.push(
        renderItem(
          "project",
          t("generic.project"),
          "project",
          <Folder />,
          !!(stageId || procedureId || activityId)
        )
      );
    }

    if (activityId && !isTender) {
      items.push(
        renderItem(
          "activity",
          t("generic.activity"),
          "activity",
          <TrendingUp />,
          !!(stageId || procedureId)
        )
      );
    }

    if (procedureId) {
      items.push(
        renderItem(
          "procedure",
          t("generic.procedure"),
          "procedure",
          <Assignment />,
          !!stageId
        )
      );
    }

    if (stageId) {
      items.push(
        renderItem(
          "stage",
          stage?.stageTypeLabel ? t(stage.stageTypeLabel) : t("generic.stage"),
          "stage",
          <Layers />,
          false
        )
      );
    }

    return items;
  };

  useEffect(() => {
    if (stageId && procedureId && projectId && activityId) {
      setRoleLoading(true);
      setStageLoading(true);

      getStageRole(
        Number(projectId),
        Number(activityId),
        Number(procedureId),
        Number(stageId)
      )
        .then((res) => {
          setStageRole(res);
          setProcedureRole(undefined);
        })
        .finally(() => setRoleLoading(false));

      getStage(
        Number(projectId),
        Number(activityId),
        Number(procedureId),
        Number(stageId)
      )
        .then(setStage)
        .finally(() => setStageLoading(false));

    } else if (procedureId && projectId && activityId) {
      setRoleLoading(true);
      setStage(undefined);

      getProcedureRole(
        Number(projectId),
        Number(activityId),
        Number(procedureId)
      )
        .then((res) => {
          setProcedureRole(res);
          setStageRole(undefined);
        })
        .finally(() => setRoleLoading(false));
    } else {
      setStageRole(undefined);
      setProcedureRole(undefined);
      setStage(undefined);
    }
  }, [stageId, procedureId, projectId, activityId]);

  return (
    <StyledBreadcrumContainer>
      <Breadcrumbs 
        separator={<Typography sx={{ color: "text.secondary", px: 0.5, fontWeight: 700 }}>›</Typography>} 
        aria-label="breadcrumb"
      >
        {buildBreadcrumb()}
      </Breadcrumbs>
    </StyledBreadcrumContainer>
  );
};